import { createPortal } from "react-dom";
import { LoaderCircle, X } from "lucide-react";
import { useEffect, useState } from "react";
import { apiClient, type CenaRecalcItem, type CenaRecalcRun } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { Pager } from "@/shared/components/Pager";
import { useDismiss } from "@/shared/hooks/useDismiss";
import { CENA_RECALC_OUTCOME_LABELS } from "@/shared/vocabulary/cena-recalc-parts";
import { icenaChange, noteLabels, partLabels } from "@/features/mentions/cena-recalc-view";

const PAGE_SIZE = 25;

/**
 * O detalhe de um recálculo do CENA, menção a menção — paginado, com
 * "só as que mudaram" ligado por padrão (é o que a pessoa veio ver).
 * Falha de carga fica no próprio painel, com a página anterior intacta.
 */
export function CenaRecalcResult({
  token,
  run,
  onClose
}: {
  token: string;
  run: CenaRecalcRun;
  onClose: () => void;
}) {
  const [page, setPage] = useState(1);
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [items, setItems] = useState<CenaRecalcItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useDismiss(true, onClose);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiClient
      .listCenaRecalcItems(token, run.id, { page, size: PAGE_SIZE, onlyChanged })
      .then(result => {
        if (!alive) return;
        setItems(result.items);
        setTotal(result.total);
        setError(null);
      })
      .catch(failure => {
        if (alive) setError(errorMessage(failure, "Não foi possível carregar o detalhe."));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [token, run.id, page, onlyChanged]);

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container bulk-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cena-recalc-result-title"
        onClick={event => event.stopPropagation()}
      >
        <header className="modal-header">
          <div>
            <span className="eyebrow">CENA</span>
            <h2 id="cena-recalc-result-title">Detalhes do recálculo</h2>
            <p className="bulk-dialog-target">{partLabels(run.parts).join(" · ")}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Fechar">
            <X size={18} />
          </button>
        </header>
        <div className="modal-body">
          <label className="filter-check">
            <input
              type="checkbox"
              checked={onlyChanged}
              onChange={event => {
                setOnlyChanged(event.target.checked);
                setPage(1);
              }}
            />
            <span>Só as que mudaram</span>
          </label>
          {error && (
            <div className="account-feedback error" role="alert">
              {error}
            </div>
          )}
          {loading && items.length === 0 ? (
            <p className="ai-reprocess-hint">
              <LoaderCircle className="spin" size={14} aria-hidden="true" /> Carregando…
            </p>
          ) : items.length === 0 && !error ? (
            <p className="ai-reprocess-hint">
              {onlyChanged ? "Nenhuma menção mudou de nota." : "Nenhuma menção neste recálculo."}
            </p>
          ) : (
            <ul className="ai-reprocess-sample-list">
              {items.map(item => (
                <li key={item.link_id} className="ai-reprocess-card">
                  <strong>{item.title || "Publicação sem título"}</strong>
                  <p>
                    {[item.brand_name, item.channel].filter(Boolean).join(" · ")}
                    {" — "}
                    <span>{CENA_RECALC_OUTCOME_LABELS[item.outcome]}</span>
                  </p>
                  <p>
                    <strong>{icenaChange(item)}</strong>
                    {item.changed_parts.length > 0 &&
                      ` (${partLabels(item.changed_parts).join(", ")})`}
                  </p>
                  {item.notes.length > 0 && (
                    <p className="ai-reprocess-hint">{noteLabels(item.notes).join("; ")}.</p>
                  )}
                  {item.error && <em>{item.error}</em>}
                </li>
              ))}
            </ul>
          )}
          <Pager page={page} size={PAGE_SIZE} count={{ total }} onPage={setPage} busy={loading} />
        </div>
        <footer className="modal-footer">
          <button type="button" className="secondary-button" onClick={onClose}>
            Fechar
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}
